import { buildSelectionSwitchBlock } from './demoSelectionSwitch.js'
import {
  GCP_HOSTING_GCP_APP_BLOCK,
  GCP_HOSTING_PLAN_MARKDOWN,
  buildPhaseStartBlocks,
} from './gcpHostingDemoContent.part02.js'
import {
  PHASE1_DONE_TABLE,
  PHASE1_START_TABLE,
  PHASE2_DONE_TABLE,
  PHASE3_DONE_TABLE,
  PHASE4_DONE_TABLE,
  PHASE5_DONE_TABLE,
  PHASE6_DONE_TABLE,
  PHASE7_DONE_TABLE,
  PHASE8_DONE_TABLE,
  PHASE9_DONE_TABLE,
  buildPhaseCompleteBlocks,
} from './gcpHostingDemoContent.part03.js'
import { GCP_HOSTING_WRAP_UP_MARKDOWN } from './gcpHostingDemoContent.part04.js'
import { gcpHostingDeployLogBlock } from './gcpHostingDemoContent.part06.js'

export function buildGcpHostingFinalBlocks(opts = {}) {
  return [
    { type: 'markdown', content: GCP_HOSTING_PLAN_MARKDOWN },
    GCP_HOSTING_GCP_APP_BLOCK,
    buildSelectionSwitchBlock('agent', 'Research', 'Software Engineering'),
    buildSelectionSwitchBlock('persona', 'Sales', 'Architect'),
    {
      type: 'markdown',
      content: '*Provisioning GCP infrastructure — execution log below.*',
      variant: 'muted',
    },
    gcpHostingDeployLogBlock(opts),
    ...buildPhaseStartBlocks(
      1,
      'Project Setup',
      'Setting up your Google Cloud project, enabling required services, and creating a secure service account.',
      'This is the foundation. Everything else will be built on top of this.',
      'Starting now… (usually takes 1–2 minutes)',
    ),
    { type: 'heading', level: 4, content: 'Status Dashboard' },
    {
      type: 'markdown',
      content: '*Initial deployment checklist — I will update this after each phase.*',
      variant: 'muted',
    },
    PHASE1_START_TABLE,
    { type: 'markdown', content: "I'll update you when this phase is complete.", variant: 'muted' },
    ...buildPhaseCompleteBlocks(
      'Phase 1 Complete – Project is ready!',
      PHASE1_DONE_TABLE.data,
      '**Ready for Phase 2**',
    ),
    { type: 'markdown', content: 'Your Google Cloud project is now configured with all necessary permissions.', variant: 'muted' },

    ...buildPhaseStartBlocks(
      2,
      'Secure Database',
      'Provisioning a private Cloud SQL PostgreSQL instance with VPC peering — no public internet access.',
      'Your B2B data stays isolated inside Google\'s network.',
      'Estimated time: 4–6 minutes',
    ),
    ...buildPhaseCompleteBlocks(
      'Phase 2 Complete – Secure Database is Ready!',
      PHASE2_DONE_TABLE.data,
      '**Ready for Phase 3**',
    ),
    { type: 'markdown', content: 'Your PostgreSQL database is now running privately (no public access).', variant: 'muted' },

    ...buildPhaseStartBlocks(
      3,
      'Building & Deploying Backend',
      'Creating your Python backend (FastAPI), adding JWT login, organization & deal management, and deploying it to Cloud Run.',
      'This is the secure "brain" of your B2B platform.',
      'Estimated time: 6–8 minutes',
    ),
    ...buildPhaseCompleteBlocks(
      'Phase 3 Complete – Backend is Live!',
      PHASE3_DONE_TABLE.data,
      '**Ready for Phase 4 (Frontend)**',
    ),
    { type: 'markdown', content: 'Your Python API is now running on Cloud Run.', variant: 'muted' },

    ...buildPhaseStartBlocks(
      4,
      'Building & Uploading React Frontend',
      'Building your React website and uploading it to Google Cloud Storage with CDN caching.',
      'This makes your frontend fast and globally available.',
      'Estimated time: 3–5 minutes',
    ),
    ...buildPhaseCompleteBlocks(
      'Phase 4 Complete – Frontend is Uploaded!',
      PHASE4_DONE_TABLE.data,
      '**Ready for Phase 5**',
    ),
    { type: 'markdown', content: 'Your React app is now stored and ready.', variant: 'muted' },

    ...buildPhaseStartBlocks(
      5,
      'Setting Up Load Balancer & Security',
      `Creating a single entry point (Load Balancer) that:
- Serves your React frontend
- Routes API calls to the backend
- Adds HTTPS + Cloud Armor (protection against attacks)

This is one of the most important steps for production.`,
      'A unified entry point is required before custom domain and SSL can activate.',
      'Estimated time: 8–12 minutes',
    ),
    ...buildPhaseCompleteBlocks(
      'Phase 5 Complete – Load Balancer + Security Ready!',
      PHASE5_DONE_TABLE.data,
    ),
    { type: 'markdown', content: 'You now have a single secure URL for everything.', variant: 'muted' },

    ...buildPhaseStartBlocks(
      6,
      'SSL Certificate & Domain Activation',
      'Provisioning managed SSL and binding your custom domain once nameservers propagate.',
      'HTTPS is required for production B2B workflows and CRM webhooks.',
      'Estimated time: 5–10 minutes (after DNS)',
    ),
    ...buildPhaseCompleteBlocks(
      'Phase 6 Complete – HTTPS is Active!',
      PHASE6_DONE_TABLE.data,
      '**Ready for Phase 7**',
    ),

    ...buildPhaseStartBlocks(
      7,
      'Monitoring & Alerting',
      'Configuring Cloud Monitoring dashboards, uptime checks, and error-rate alerts for backend and frontend.',
      "You'll know immediately if anything goes wrong in production.",
      'Estimated time: 3–4 minutes',
    ),
    ...buildPhaseCompleteBlocks(
      'Phase 7 Complete – Monitoring is Live!',
      PHASE7_DONE_TABLE.data,
      '**Ready for Phase 8**',
    ),

    ...buildPhaseStartBlocks(
      8,
      'CI/CD Pipeline',
      'Setting up Cloud Build triggers for automatic deploys on push to `main`.',
      'Future updates ship without manual steps.',
      'Estimated time: 4–5 minutes',
    ),
    ...buildPhaseCompleteBlocks(
      'Phase 8 Complete – CI/CD is Ready!',
      PHASE8_DONE_TABLE.data,
      '**Ready for Phase 9**',
    ),

    ...buildPhaseStartBlocks(
      9,
      'Production Verification & Handoff',
      'Running smoke tests, generating the architecture runbook, and confirming all services are healthy.',
      'Final check before handing you the production URL.',
      'Estimated time: 2–3 minutes',
    ),
    ...buildPhaseCompleteBlocks(
      'Phase 9 Complete – NexusB2B is Live!',
      PHASE9_DONE_TABLE.data,
    ),
    { type: 'markdown', content: GCP_HOSTING_WRAP_UP_MARKDOWN },
  ]
}
